import React, { useEffect, useState, useContext } from 'react';
import { ClockCounterClockwiseIcon, CaretDoubleLeftIcon, ArrowSquareUpRightIcon, MagnifyingGlassIcon, CrosshairIcon } from "@phosphor-icons/react";
import { recordStat } from '@/shared/functions';
import { Card, CardHeader, CardBody } from "@heroui/card";
import { FeaturesContext, StatsContext } from '@/app/contexts';
import { Button } from '@heroui/react';
import { Spinner } from '@heroui/spinner';
import { Tooltip } from '@heroui/tooltip';
interface ChatSessionsProps {
  caseId: string;
  userId: string;
  sessionId: string | null;
  switchToSession: (sessionId: string) => void;
  closeSessions: () => void;
  chatSessions: any[] | null;
}
export const ChatSessions: React.FC<ChatSessionsProps> = ({
  caseId,
  userId,
  sessionId,
  switchToSession,
  closeSessions,
  chatSessions
}: ChatSessionsProps) => {
  const [chatHistorySessions, setChatHistorySessions] = useState<any>(null);
  const [localSessionId, setLocalSessionId] = useState(sessionId);
  useEffect(() => {
    setLocalSessionId(sessionId);
  }, [sessionId]);
  useEffect(() => {
    setChatHistorySessions(chatSessions);
  }, [chatSessions]);
  const statsContext = useContext(StatsContext);
  const {
    featureFlags
  } = useContext(FeaturesContext) || {};
  return <Card classNames={{
    base: `box-border bg-white/5 flex flex-col items-center border-1 border-solid border-white/0
          shadow-none overflow-y-auto p-2 sm:p-4 rounded-md ml-[18px] mt-[13px]
        `,
    header: 'box-border flex flex-row justify-between gap-2 items-center p-0 m-0 pb-1 mb-2 rounded-none border-b-[3px] border-white border-double',
    body: 'box-border m-0 p-0 custom-scrollbar2 overflow-y-auto'
  }} data-sentry-element="Card" data-sentry-component="ChatSessions" data-sentry-source-file="chat-sessions.tsx">
      <CardHeader data-sentry-element="CardHeader" data-sentry-source-file="chat-sessions.tsx">
        <div className="flex flex-row items-center gap-1">
          <ClockCounterClockwiseIcon size={25} weight="light" data-sentry-element="ClockCounterClockwiseIcon" data-sentry-source-file="chat-sessions.tsx" />
          <h4 className='m-0 p-0 font-light text-lg'>Session History</h4>
        </div>
        <Tooltip content={'Dock session history'} crossOffset={2} closeDelay={300} showArrow={true} classNames={{
        content: 'w-[fit-content] max-w-[300px] h-[fit-content] rounded-md p-2 bg-tooltip-bg text-white',
        base: 'before:bg-tooltip-bg'
      }} data-sentry-element="Tooltip" data-sentry-source-file="chat-sessions.tsx">
          <Button onPress={closeSessions} isIconOnly className="box-border flex px-1 py-0.5 m-0 min-w-[30px] w-[30px] min-h-[30px] h-[30px] bg-transparent hover:bg-white/5 border-1 border-white/0 border-solid text-white hover:text-white cursor-pointer rounded-sm" data-sentry-element="Button" data-sentry-source-file="chat-sessions.tsx">
            <CaretDoubleLeftIcon size={18} weight="bold" className="m-0 p-0" data-sentry-element="CaretDoubleLeftIcon" data-sentry-source-file="chat-sessions.tsx" />
          </Button>
        </Tooltip>
      </CardHeader>
      <CardBody data-sentry-element="CardBody" data-sentry-source-file="chat-sessions.tsx">
        {chatHistorySessions && chatHistorySessions?.length > 0 ? <ol className="flex flex-col gap-1 counter-list list-none m-0 p-0 pb-3">
            {chatHistorySessions && chatHistorySessions?.length > 0 && chatHistorySessions.map((session: any, index: number) => {
          return <li key={`chat-session-${session.sessionId}`}>
                  <button data-id={`sessionId-${session.sessionId}`} disabled={session.sessionId === localSessionId} className="box-border flex flex-row gap-1 items-center justify-between px-1 rounded-sm cursor-pointer disabled:cursor-not-allowed" onClick={event => {
              event.stopPropagation();
              if (session.sessionId !== localSessionId) {
                switchToSession(session.sessionId);
                recordStat('SwitchToSessionButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
              }
            }}>
                    <Tooltip content={<div className='flex flex-col gap-1 py-0'>
                          {session?.sessionId === localSessionId ? <label className='text-brand-blue text-md font-bold'>Current Session Prompts</label> : <label className='text-brand-blue text-md font-bold'>Session Prompts</label>}
                          <ol key={session.sessionId} className='list-decimal list-outside pl-0 ml-2 mt-0'>
                            {session?.questions?.map((question: any, index: number) => <li key={index} className='text-balance'>
                                <span>{`${question.question}`}</span>
                                {question.isDeepDive && <CrosshairIcon size={14} className="text-blue-600 relative mt-[2px] -mb-[2px]" />}
                              </li>)}
                          </ol>
                        </div>} delay={500} showArrow={true} placement="right" portalContainer={document.body} className='flex flex-row gap-1 items-start justify-start max-w-[350px] rounded-md bg-white text-gray-700' classNames={{
                base: 'before:bg-white max-w-[350px]',
                content: 'text-wrap p-4 max-h-[200px] overflow-y-auto custom-scrollbar3 pb-0 mb-0'
              }}>
                      <div className={`flex flex-col mr-[10px] text-[12px] w-full rounded-md gap-1 p-2 m-0 hover:bg-white/5
                          ${session.sessionId === localSessionId ? 'cursor-not-allowed border-1 border-white/40 border-solid bg-white/10' : 'cursor-pointer border-1 border-white/5 border-dashed'}
                        `}>
                        <div className='box-border flex flex-row justify-between gap-1 text-white bg-white/5 px-1 py-0.5 rounded-sm'>
                          <div className='flex flex-row gap-1 items-center justify-center'>
                            {session?.questions[session?.questions?.length - 1]?.epochTime ? new Date(session?.questions[session?.questions?.length - 1]?.epochTime * 1000).toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      }) : '--'}
                          </div>
                          <div className='flex flex-row gap-[1px] items-center justify-center'>
                            <MagnifyingGlassIcon size={12} weight="duotone" />
                            {session?.questions?.length}
                          </div>
                          <div className='flex flex-row gap-1 items-center justify-center'>
                            <ArrowSquareUpRightIcon size={15} weight="duotone" />
                          </div>
                        </div>
                        
                        <div className='box-border flex flex-row gap-1 ml-1'>
                          <div className='text-white/65 italic line-clamp-3 text-ellipsis'>
                            &ldquo;{session?.questions[session?.questions?.length - 1]?.question}&rdquo;
                          </div>
                        </div>
                      </div>
                    </Tooltip>
                  </button>
                </li>;
        })}
          </ol> : <>
            {!chatHistorySessions ? <div className="h-[45px] w-full flex items-center justify-center bg-transparent">
                <Spinner size="md" color="white" className="bg-transparent" />
              </div> : <div className="h-[45px] w-full flex items-center justify-center bg-transparent">
                <div className="text-white text-md font-light">No chat history found</div>
              </div>}
          </>}
      </CardBody>
    </Card>;
};